import React from "react"
import PropTypes from "prop-types"
import { Column, SortDirection } from "react-virtualized"

import CSVButton from "@swipesense/csv-button"
import DataTable, { tooltipHeaderRenderer } from "@swipesense/data-table"

import Loading from "../Loading"
import PeakUsageGraph from "../PeakUsageGraph"
import UtilizationPercentSquare from "../UtilizationPercentSquare"
import "./UtilizationUnitDetailTable.scss"

class UtilizationUnitDetailTable extends React.PureComponent {
  peakUsageTrendRenderer = ({ cellData }) => {
    if (cellData.peakUsageTrend.length === 0) {
      return "-"
    } else {
      return (
        <PeakUsageGraph
          bare={true}
          maxInEntityCount={100} // Instead of graphing the peak usage as a count, we're actually graphing it as a percentage of all assets over time. Hence, the max is 100%.
          timeslices={cellData.peakUsageTrend}
        />
      )
    }
  }

  avgUsageRenderer = ({ cellData }) => UtilizationPercentSquare(cellData)

  unitRenderer = ({ cellData }) => {
    return <span className="unit-name">{cellData.unitName}</span>
  }

  // The last row needs to be slightly taller so it isn't scrunched against the bottom.
  rowHeight = ({ index }) => {
    return index + 1 === this.props.data.length ? 60 : 44
  }

  rowClassName = ({ index }) => {
    return index + 1 === this.props.data.length ? "last" : ""
  }

  render() {
    return (
      <div className="UtilizationUnitDetailTable">
        <h3>Units using {this.props.deviceName}</h3>
        <CSVButton
          filename={`SwipeSense Asset Utilization Unit Detail ${new Date().toLocaleDateString()}.csv`}
          data={this.props.data}
          headers={[
            "In Unit",
            "Avg Qty Used",
            "# Avail During Peak",
            "Avg Usage",
          ]}
          mapData={row => [
            row.unitName,
            row.averageUtilizedCount,
            row.availableDuringPeak,
            row.avgUsage,
          ]}
        />
        <div className="table-container">
          <DataTable
            data={this.props.data}
            rowHeight={this.rowHeight}
            rowClassName={this.rowClassName}
            sortBy={this.props.sortBy}
            sortDirection={this.props.sortDirection}
            onSortChange={this.props.onSortChange}
            noRowsMessage="No data found. Try a new search or date range."
            loadingComponent={<Loading />}
            loading={this.props.loading}
          >
            <Column
              label="USING UNIT"
              width={1}
              dataKey="unitName"
              cellDataGetter={({ rowData }) => rowData}
              cellRenderer={this.unitRenderer}
            />
            <Column
              label="AVG QTY USED"
              width={1}
              dataKey="averageUtilizedCount"
              headerRenderer={tooltipHeaderRenderer(
                '"Average quantity used" is the number of assets in use on average (avg usage % * number of assets).'
              )}
            />
            <Column
              label="# AVAILABLE DURING PEAK"
              width={1}
              dataKey="availableDuringPeak"
              headerRenderer={tooltipHeaderRenderer(
                '"# Available during peak" is the lowest number of assets available for use over your selected date range.'
              )}
            />
            <Column
              label="AVG USAGE"
              width={1}
              dataKey="avgUsage"
              cellRenderer={this.avgUsageRenderer}
              headerRenderer={tooltipHeaderRenderer(
                '"Average usage" is the percent of time assets spent in a patient room, and likely in use, over your selected date range.'
              )}
            />
            <Column
              label="PEAK USAGE TREND"
              width={1}
              dataKey="peakUsageTrend"
              cellRenderer={this.peakUsageTrendRenderer}
              cellDataGetter={({ rowData }) => rowData}
              disableSort={true}
              headerRenderer={tooltipHeaderRenderer(
                '"Peak usage trend" represents your peak usage plotted over your selected date range.'
              )}
            />
          </DataTable>
        </div>
      </div>
    )
  }
}

UtilizationUnitDetailTable.defaultProps = {
  data: [],
  sortBy: "unitName",
  sortDirection: SortDirection.ASC,
  loading: false,
}

UtilizationUnitDetailTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  deviceName: PropTypes.string,
  onSortChange: PropTypes.func.isRequired,
  sortBy: PropTypes.string.isRequired,
  sortDirection: PropTypes.oneOf([SortDirection.ASC, SortDirection.DESC])
    .isRequired,
  loading: PropTypes.bool,
}

export default UtilizationUnitDetailTable
